import * as TabsPrimitive from '@radix-ui/react-tabs';
import {
  type ComponentPropsWithoutRef,
  createContext,
  forwardRef,
  useContext,
  useRef,
} from 'react';
import { cn } from '../../lib/utils';
import { GlassSlider, useGlassSlider } from './glass-slider';

/**
 * Theo quy ước shadcn/ui, cùng cách bọc Radix như `dropdown-menu.tsx`: mũi
 * trái/phải chuyển tab, focus quay lại đúng chỗ, và trạng thái "đang chọn"
 * đến từ `aria-selected` thay vì so sánh chuỗi thủ công.
 */
export const Tabs = TabsPrimitive.Root;

/** Nội dung của một tab — Radix tự ẩn tab không chọn và nối `aria-controls`. */
export const TabsContent = TabsPrimitive.Content;

/**
 * Lớp của `TabsList` và `TabsTrigger`, tách thành hằng số — APM-PSET-g.
 *
 * Xuất ra vì `project-tabs-row.tsx` dựng một **bản đo** của cả dải tab để biết
 * mỗi tab rộng bao nhiêu trước khi quyết định giấu tab nào vào menu. Bản đo ấy
 * không thể dùng chính `TabsTrigger`: hai `TabsList` trong cùng một `Tabs` sẽ
 * đăng ký trùng `value`, và roving focus của Radix sẽ nhảy sang bản vô hình.
 *
 * Nên bản đo là `<span>` thường mang **đúng** bộ lớp này. Sửa lớp ở đây mà quên
 * xuất ra thì phép đo lệch khỏi thứ đang vẽ, và dải tab hoặc thừa chỗ hoặc tràn
 * — hỏng im lặng, vì cả hai đều trông như một bố cục bình thường.
 */
export const TABS_LIST_CLASS =
  'inline-flex h-9 w-fit items-center gap-1 rounded-DEFAULT border border-border bg-muted/40 p-1';

export const TAB_TRIGGER_CLASS = [
  'inline-flex h-7 items-center justify-center whitespace-nowrap rounded-[calc(var(--radius-DEFAULT)-2px)] px-3 text-sm font-medium text-muted-foreground transition-colors',
  'hover:text-foreground',
  'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
  'disabled:pointer-events-none disabled:opacity-50',
].join(' ');

/** Khoảng cách giữa hai tab trong `TabsList` (`gap-1`), tính bằng px. */
export const TABS_LIST_GAP = 4;

/*
  Biến thể **kính** — cùng chất liệu với thanh điều hướng (người dùng yêu cầu
  2026-09-28 cho các tab trong Việc của tôi, Hiệu suất, OKR): viên kính bo tròn,
  tab đang chọn là thấu kính xám **trượt** tới nó (`useGlassSlider`), chữ đậm.

  Là một biến thể chứ không thay kiểu mặc định: dải tab dự án đo bề rộng tab
  bằng đúng `TAB_TRIGGER_CLASS` (xem trên), và mười chỗ khác đang dùng kiểu cũ.

  Xuất lớp ra để một cụm nút tự viết (Danh sách/Lịch — không phải tab của Radix)
  mặc được cùng bộ áo, với `aria-current` thay cho `data-state`.
*/
export const GLASS_TABS_LIST_CLASS =
  'glass-bar inline-flex h-9 w-fit items-center gap-0.5 rounded-full p-0.75';

/** `gap-0.5` của `GLASS_TABS_LIST_CLASS`, tính bằng px — cùng vai với `TABS_LIST_GAP`. */
export const GLASS_TABS_LIST_GAP = 2;

/** Menu thả xuống cạnh một dải tab kính — cùng áo với menu của thanh điều hướng. */
export const GLASS_MENU_CLASS = 'glass-menu rounded-2xl p-1.5';

export const GLASS_TAB_TRIGGER_CLASS = [
  'glass-hover relative inline-flex h-full items-center justify-center whitespace-nowrap rounded-full px-3.5 pt-px text-sm text-muted-foreground',
  'hover:text-foreground',
  'data-[state=active]:font-semibold data-[state=active]:text-foreground',
  'aria-[current=true]:font-semibold aria-[current=true]:text-foreground',
  'disabled:pointer-events-none disabled:opacity-50',
].join(' ');

type TabsVariant = 'default' | 'glass';

/** `TabsTrigger` hỏi `TabsList` đang mặc áo nào, để khỏi phải truyền `variant` từng tab. */
const TabsVariantContext = createContext<TabsVariant>('default');

export const TabsList = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { variant?: TabsVariant }
>(({ className, variant = 'default', ...props }, ref) =>
  variant === 'glass' ? (
    <GlassTabsList ref={ref} className={className} {...props} />
  ) : (
    <TabsPrimitive.List ref={ref} className={cn(TABS_LIST_CLASS, className)} {...props} />
  ),
);
TabsList.displayName = 'TabsList';

const GlassTabsList = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useGlassSlider(listRef, '[role="tab"][data-state="active"]');

  return (
    <TabsVariantContext.Provider value="glass">
      <TabsPrimitive.List
        ref={(node) => {
          listRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className={cn(GLASS_TABS_LIST_CLASS, className)}
        {...props}
      >
        {/* Con đầu tiên: các tab vẽ sau nên nằm đè lên viên. Radix chỉ gom
            `Trigger` vào vòng focus, nên một `<span>` ở đây không làm lệch
            phím mũi tên. */}
        <GlassSlider ref={sliderRef} />
        {children}
      </TabsPrimitive.List>
    </TabsVariantContext.Provider>
  );
});
GlassTabsList.displayName = 'GlassTabsList';

export const TabsTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(variant === 'glass' ? GLASS_TAB_TRIGGER_CLASS : TAB_TRIGGER_CLASS, className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = 'TabsTrigger';
